import { FC } from 'react';
import { BsBuilding, BsHouseFill, BsPersonFill } from 'react-icons/bs';

import { Box, styled, themeGet } from '@qwealth/qcore';

import { StyledSectionHeaderLink } from 'components/common/SectionHeader/HeaderLinks/ContentLinks';
import InfoTag from 'components/common/SectionHeader/InfoTag';
import {
  ClientComplianceReview,
  COMPLIANCE_CLIENT_RTQ_LEVEL_CORPORATE,
  COMPLIANCE_CLIENT_RTQ_LEVEL_INDIVIDUAL,
  COMPLIANCE_CLIENT_RTQ_LEVEL_HOUSEHOLD,
  COMPLIANCE_CHECKLIST_STATUS_ADVISOR_ACTION_REQUIRED,
  COMPLIANCE_CHECKLIST_STATUS_ACTION_REQUIRED,
} from 'data/models/Compliance';
import { BASE_DATE_FORMAT, formatDate } from 'data/helpers/dateTimeHelper';

const ReviewItemContainer = styled(Box)`
  width: 100%;
  height: fit-content;
  padding: ${themeGet('space.default')};
  border-radius: ${themeGet('radii.large')};
  display: flex;
  flex-direction: column;
  background-color: ${themeGet('colors.gray.4')};

  &:hover {
    background-color: ${themeGet('colors.gray.3')};
  }
`;

interface ReviewItemProps {
  contact: ClientComplianceReview | undefined;
}

const ReviewItem: FC<ReviewItemProps> = ({ contact }): JSX.Element => {
  const getIPSAcceptanceDate = () => {
    if (contact?.ipsAcceptanceDate) {
      return formatDate(contact.ipsAcceptanceDate, BASE_DATE_FORMAT);
    }
    return 'No Sign Date';
  };

  const ipsAcceptanceDate = getIPSAcceptanceDate();

  return (
    <ReviewItemContainer>
      <Box display="flex" flexDirection="row">
        <Box
          backgroundColor="blueTransparent"
          display="flex"
          width="fit-content"
          padding="default"
          borderRadius="large"
          maxHeight="62px"
          maxWidth="62px"
        >
          {contact?.rtqLevel === COMPLIANCE_CLIENT_RTQ_LEVEL_HOUSEHOLD && <BsHouseFill fill="#3772B8" size={40} />}
          {contact?.rtqLevel === COMPLIANCE_CLIENT_RTQ_LEVEL_CORPORATE && <BsBuilding fill="#3772B8" size={40} />}
          {contact?.rtqLevel === COMPLIANCE_CLIENT_RTQ_LEVEL_INDIVIDUAL && <BsPersonFill fill="#3772B8" size={40} />}
        </Box>

        <Box marginLeft="large" marginRight="large" display="flex" flexDirection="column" width="100%">
          <Box display="flex" flexDirection="row" gap="large" fontSize="sectionTitle" fontWeight="bold" alignItems="center" marginBottom="default">
            {contact && contact.name}
          </Box>
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <Box display="flex" flexDirection="row" fontSize="default" gap="default" alignItems="center">
              <Box fontWeight="bold">Assigned:</Box>
              {contact && <Box>{contact.assignee}</Box>}
            </Box>
            <Box display="flex" flexDirection="row" fontSize="default" gap="default" alignItems="center">
              <Box fontWeight="bold">Status:</Box>
              {contact?.complianceChecklistStatus && <InfoTag tagTitle={contact.complianceChecklistStatus} />}
            </Box>
          </Box>
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" width="100%">
            {contact && (
              <Box display="flex" flexDirection="row" alignItems="center" gap="default">
                <Box fontWeight="bold">Household:</Box>
                <StyledSectionHeaderLink href={`/household?household=${contact.household.QID}`}>
                  <Box>
                    <BsHouseFill size={16} />
                  </Box>
                  <Box paddingTop="small" marginLeft="small">
                    {contact.household.name}
                  </Box>
                </StyledSectionHeaderLink>
              </Box>
            )}

            {contact && (
              <Box display="flex" flexDirection="row" gap="default" alignItems="center">
                <Box fontWeight="bold">Risk Level:</Box>
                <InfoTag tagTitle={contact.rtqLevel} />
              </Box>
            )}
          </Box>
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" width="100%">
            <Box display="flex" flexDirection="row" alignItems="center" gap="default">
              <Box fontWeight="bold">Partner:</Box>
              <Box> {contact && contact.partnerName} </Box>
            </Box>

            <Box display="flex" flexDirection="row" gap="default" alignItems="center">
              <Box fontWeight="bold">IPS Date:</Box>
              <Box> {ipsAcceptanceDate} </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </ReviewItemContainer>
  );
};

export default ReviewItem;
